import { getDomain } from '@/utils/getDomain';
import { removeTrailingSlash } from '@/utils/removeTrailingSlash';

export const rewriteAnchors = (type: string, bodytext: any, domain: string = getDomain(false)): string | null => {
  if (!bodytext || typeof bodytext !== 'string') return bodytext;

  const pattern = /(<a .+?>)(.+?)(<\/a>)/g; // <- g flag will maintain lastIndex across calls
  pattern.lastIndex = 0;

  let content = bodytext;
  let linkNode = pattern.exec(content);

  while (linkNode) {
    const [outerHtml, openingTag, innerHtml, _] = linkNode;
    const [res, to] = rewriteAnchor([outerHtml, openingTag, innerHtml], domain);
    if (res === 'replace' && to) {
      content = content.substring(0, pattern.lastIndex - outerHtml.length) + to + content.substring(pattern.lastIndex);
      pattern.lastIndex = pattern.lastIndex - outerHtml.length + to.length; // adjust lastIndex after replacement
    }

    linkNode = pattern.exec(content);
  }
  return content;
};

export type RewriteAnchorResult = [result: 'none' | 'replace', to?: string];
/**
 * Rewrite a single link. The input is the original full content, possibly containing multiple links,
 * and the matched link parts.
 */
export const rewriteAnchor = (
  [outerHtml, openingTag, innerHtml]: string[],
  domain: string = getDomain(false),
): RewriteAnchorResult => {
  const [_, originalHref = ''] = openingTag.match(/href="(.+?)"/) ?? [];
  if (originalHref) {
    const [res, newHref] = rewriteUrl(originalHref, domain);
    if (res === 'removeAnchor') {
      // remove anchor tag, keep inner html
      return ['replace', innerHtml];
    } else if (res === 'rewritten') {
      const newOpeningTag = openingTag.replace(`href="${originalHref}"`, `href="${newHref}"`);
      const newOuterHtml = `${newOpeningTag}${innerHtml}</a>`;
      return ['replace', newOuterHtml];
    }
  }

  return ['none'];
};

export type RewriteUrlResult = [result: 'none' | 'rewritten' | 'removeAnchor', href?: string];

/**
 * Rewrite a single URL if needed.
 */
export const rewriteUrl = (url: string | undefined | null, domain: string = getDomain(false)): RewriteUrlResult => {
  if (!url) return ['none'];
  const domainName = domain?.match(/https?:\/\/([^/]+)/)?.[1] || '';
  const domainPrefix = `https://${domainName}`;
  const unsecureDomainPrefix = `http://${domainName}`;

  let href = url;
  if (href.startsWith(unsecureDomainPrefix)) href = href.replace(unsecureDomainPrefix, ''); // it it is a http link to our site, make it relative
  // only look at internal links
  if (href.startsWith(domainPrefix) || href.startsWith('/')) {
    if (href.startsWith(domainPrefix)) href = href.substring(domainPrefix.length); // make internal links relative
    if (href.startsWith('/tag')) return ['removeAnchor']; // remove /tag links completely, keep inner html

    if (href.startsWith('/etikett')) {
      // example: https://www.hant.se/etikett/gabriel odenhammar/www.hant.se/www.hant.se/
      if (href.endsWith(`${domainName}/`)) href = href.replaceAll(`${domainName}/`, '');
      // remove trailing slash from /etikett links
      if (href.endsWith('/')) href = href.substring(0, href.length - 1);
      // make /etikett links lowercase and replace %20 with space
      href = href.toLowerCase().replaceAll('%20', ' ');
    }

    if (href.startsWith('/video/tag/')) {
      // remove trailing slash from /video/tag links
      if (href.endsWith('/')) href = removeTrailingSlash(href);
    }

    if (href != url) return ['rewritten', href];
  }

  return ['none'];
};

// drop in function to normalize url's - doesn't handle removal of anchors however
export const rewriteLink = (url: string | undefined | null): string => {
  if (!url) return '';
  const [res, newHref] = rewriteUrl(url);
  if (res === 'rewritten' && newHref) return newHref;
  return url;
};
